import React, { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Link, Outlet } from 'react-router-dom'
import {
  AsyncSelectInput,
  CardRadioInput,
  Checkbox,
  SelectInput,
  StatsCard,
  Table,
  TextInput,
  ToggleInput,
} from '@demandlocal/components'
import { useGetCustomers } from '../../../../api/dealership/customers/useGetCustomers'
import { Customer } from '@demandlocal/utils/lib/mongo/models/Customers'
import { createColumnHelper } from '@tanstack/react-table'
import { useGetOverallStats } from '../../../../api/dealership/useGetOverallStats'
import LoadingAnimation from '../../../../components/LoadingAnimation'
import { useQueryParams } from '../../../../lib/hooks/useQueryParams'
import { useGetCustomerStats } from '../../../../api/dealership/customers/useGetCustomersStats'
import { formatNumber } from '../../../../lib/utils/formatNumber'
import * as portals from 'react-reverse-portal'
import FiltersContext from '../../../../components/Filters/FiltersContext'
import { useGetCustomerSources } from '../../../../api/dealership/customers/useGetCustomerSources'
import dayjs from 'dayjs'
import { ReactComponent as IconCheck } from './../../../../images/icons/icon-rounded-check.svg'
import { ReactComponent as IconCross } from './../../../../images/icons/rounded_cross.svg'

interface CustomerType extends Customer {
  id: string
  full_name: string
  last_event: string
  firstActivity: string
  lastActivity: string
  hasRecentLead: boolean
  hasRecentPurchase: boolean
}
const columnHelper = createColumnHelper<CustomerType>()

export default function CustomersList(): JSX.Element {
  const queryParams = useQueryParams()
  const filtersPortalNode = useMemo(() => portals.createHtmlPortalNode(), [])

  const [filters, setFilters] = useState<Partial<any>>({})
  const { data: customers } = useGetCustomers({
    filters: filters,
    queryOptions: {
      keepPreviousData: true,
    },
  })
  const {
    data: {
      sources = {
        total: 0,
      },
    } = {},
    isLoading,
  } = useGetOverallStats()
  const { data: customersStats } = useGetCustomerStats()
  const parsedQueryParams = useMemo(() => {
    const qp = {}
    for (const [key, value] of queryParams) qp[key] = value

    return qp
  }, [queryParams])
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'full_name',
        header: 'Name',
        cell: ({ row }) => {
          const name = `${row.original.first_name ?? ''} ${row.original.last_name ?? ''}`
          const parsedNameWithNoSpaces = name.replace(/ /g, '')
          return (
            <Link
              className={'text-primary-persian-blue font-bold'}
              to={`${row.original.id}`}>
              {parsedNameWithNoSpaces?.length > 0 ? name : 'N/A'}
            </Link>
          )
        },
      }),
      columnHelper.accessor('firstActivity', {
        header: 'First Activity',
        cell: ({ row }) => dayjs(row.original.firstActivity).format('MM/DD/YYYY'),
      }),
      columnHelper.accessor('lastActivity', {
        header: 'Last Activity',
        cell: ({ row }) => dayjs(row.original.lastActivity).format('MM/DD/YYYY'),
      }),
      columnHelper.display({
        id: 'hasRecentLead',
        header: 'Submitted lead (90 days)',
        cell: ({ row }) => {
          if (row.original.hasRecentLead) return <IconCheck />

          return <IconCross />
        },
      }),
      columnHelper.display({
        id: 'hasRecentPurchase',
        header: 'Purchased (90 days)',
        cell: ({ row }) => {
          if (row.original.hasRecentPurchase) return <IconCheck />

          return <IconCross />
        },
      }),
    ],
    [],
  )

  if (isLoading) {
    return (
      <div className={'flex flex-1 flex-col h-full'}>
        <div
          className={'flex flex-1 flex-col my-auto mx-auto items-center justify-center'}>
          <LoadingAnimation title={'Loading'} />
        </div>
      </div>
    )
  }

  if (sources.total === 0) return <></>

  return (
    <div className={'flex flex-1 flex-col px-10'}>
      <span className="text-md pb-5">
        View every customer record you have imported, see their data and customer journey
        along with contact details, and household details by clicking on their name. Some
        customers are hidden due to PII rules. Think of this as your CDP.
      </span>
      <motion.div
        layout={'preserve-aspect'}
        className={`flex gap-5 min-w-max w-full flex-row flex-nowrap`}>
        <StatsCard
          title="Total Customers"
          content={formatNumber(customersStats?.customers)}
          color="bg-primary-purple/20"
        />
        <StatsCard
          title="Bought a Vehicle"
          content={customersStats?.sales ? formatNumber(customersStats?.sales) : 'N/A'}
          color="bg-light-cyan"
        />
        <StatsCard
          title="Serviced Their Car"
          content={customersStats?.service ? formatNumber(customersStats.service) : 'N/A'}
          color="bg-champagne-pink"
        />
        <StatsCard
          title="Submitted a Lead"
          content={customersStats?.leads ? formatNumber(customersStats.leads) : 'N/A'}
          color="bg-palatinate-blue/20"
        />
      </motion.div>

      <div className={'flex flex-1 flex-col max-w-[1920px] mx-auto w-full'}>
        <motion.div className={'flex flex-1 flex-col pb-8 font-Spartan mt-12'}>
          <div className={'pb-4 flex justify-between items-start'}>
            <div className="min-w-fit flex items-center gap-2">
              <span className="font-bold text-lg">All Customers</span>
              <div
                className={'font-Spartan font-bold text-white p-2 rounded-md'}
                style={{ fontSize: 10, backgroundColor: '#121124' }}>
                {formatNumber(customers?.pagination.total)} customers
              </div>
            </div>
            <portals.OutPortal node={filtersPortalNode} />
          </div>
          <FiltersContext
            portalNode={filtersPortalNode}
            setFilters={setFilters}
            showFilters>
            <AsyncSelectInput
              isMulti
              name={'customer_source'}
              label={'Customer Source'}
              fetchFn={useGetCustomerSources}
              labelKeyName={'customer_source'}
              valueKeyName={'id'}
              searchKeyName={'customer_source'}
              displayCountOnly
            />
            <SelectInput
              isClearable
              name={'customers_with_sales'}
              label={'Customers with Sales'}
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
            />
            <SelectInput
              isClearable
              name={'customers_with_address'}
              label={'Customers with Address'}
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
            />
            <SelectInput
              isClearable
              name={'known_customers'}
              label={'Known Customers'}
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
            />
            <SelectInput
              isClearable
              name={'customers_with_leads'}
              label={'Customers with Leads'}
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
            />
            <SelectInput
              isClearable
              name={'customers_with_service'}
              label={'Customers with Service'}
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
            />
            <SelectInput
              isClearable
              name={'customers_with_impressions'}
              label={'Customers with Impressions'}
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
            />
            <SelectInput
              isClearable
              name={'customers_with_clicks'}
              label={'Customers with Clicks'}
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
            />
            <SelectInput
              isClearable
              name={'customers_with_site_visits'}
              label={'Customers with Influenced Site Visits'}
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
            />
          </FiltersContext>
          <div className={'flex flex-1 flex-col'}>
            <Table
              columns={columns}
              filters={{ ...parsedQueryParams, ...filters }}
              defaultSorting={[
                { id: 'hasRecentPurchase', desc: true },
                { id: 'site_visit_count', desc: true },
                { id: 'click_count', desc: true },
                { id: 'impression_count', desc: true },
              ]}
              fetchFn={useGetCustomers}
            />
          </div>
        </motion.div>
        <Outlet />
      </div>
    </div>
  )
}
